// Asking the bank for a paper, and the papers it gave (RFC-056 §4): an
// instructor picks a published blueprint and a class they teach — the
// classes the relay says are theirs — the window it is open and how many
// forms; the bank draws it and delivers it into their Exams on Share,
// assigned to the class. Their requests, each with where it stands and
// the papers drawn for it, linked to the exam on Share; an admin's too,
// with what waits on their decision. As rows (byj-1wv.51.18): what waits
// on an admin first, under its own heading, its decision on the row;
// asking is a dialog over the list; a paper has a page of its own — its
// items, the exam on Share, its grading — as an item and a blueprint have.
import { useCallback, useEffect, useState } from "react";
import type { BankClient, BankMe, Blueprint, GradingRow, Paper, PaperGrading, PaperRequest } from "../bank";
import { t, whenMinute, word } from "../i18n";
import { navigate } from "../router";
import { useConfirm } from "./Confirm";
import { EmptyRows, Grouped, initialOf } from "./Rows";
import { StateBar } from "./StateBar";
import { useItemTitles } from "./BankItems";
import { Choices } from "./Choices";
import { drawsLine, Trial } from "./BankBlueprints";
import { FormDialog } from "./FormDialog";
import { Link } from "./Nav";
import { RowMenu, type MenuItem } from "./RowMenu";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A datetime-local input's value as an instant, or undefined. */
const instant = (v: string) => (v ? new Date(v).toISOString() : undefined);

/** A forms count a blueprint allowing `most` takes: a whole number from 1 to `most`, anything else (0, empty, below) one. */
const formsWithin = (x: number, most: number) => Math.min(most, Math.max(1, Math.floor(x) || 1));

export const requestDialogId = "bank-request-dialog";

/** Asking for a paper: a published blueprint, a class the person teaches, its window and forms. */
export function RequestDialog({ client, me, blueprint, onDone, onClose }: {
  client: BankClient;
  me: BankMe;
  blueprint?: string;
  onDone: (r: PaperRequest) => void;
  onClose: () => void;
}) {
  const [bps, setBps] = useState<Blueprint[] | null>(null);
  const [bp, setBp] = useState(blueprint ?? "");
  const [klass, setKlass] = useState(me.user.classes[0]?.id ?? "");
  const [opens, setOpens] = useState("");
  const [closes, setCloses] = useState("");
  const [forms, setForms] = useState(1);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    client.blueprints().then(
      (l) => {
        // a paper is drawn from a blueprint's published version, under its title (byj-1wv.91). An admin is shown a draft
        // after it, with the published one's summary beside it (byj-1wv.100): that one's terms and title are what is said.
        // A draft with no published summary beside it (the server could not read it, or predates it) is offered without
        // terms, never with the draft's as though they were the published ones; its title is the list's, as nothing else
        // names the published version then
        const pub = l
          .filter((b) => b.published > 0)
          .map((b) =>
            b.published_summary
              ? { ...b, title: b.published_summary.title || b.title, summary: b.published_summary }
              : me.role === "admin" && b.latest > b.published
                ? { ...b, summary: undefined }
                : b,
          );
        setBps(pub);
        setBp((cur) => cur || pub[0]?.id || "");
      },
      (err: unknown) => setError(errText(err)),
    );
  }, [client, me.role]);
  const chosen = bps?.find((b) => b.id === bp);
  const most = Math.max(1, chosen?.summary?.forms ?? 1);
  // the field shows the count that is sent, one the chosen blueprint allows (byj-1wv.92): what is typed is brought into
  // range as it is typed, a count kept from a blueprint allowing more comes down when another is picked, and the field
  // left empty (forms 0) is sent as one form
  const count = formsWithin(forms, most);
  useEffect(() => {
    setForms((f) => (f === 0 ? 0 : formsWithin(f, most)));
  }, [most]);
  const noClass = me.user.classes.length === 0;
  return (
    <FormDialog
      id={requestDialogId}
      testid="bank-request"
      title={t("bank:reqTitle")}
      submitLabel={t("bank:reqAsk")}
      canSubmit={!!bp && !!klass}
      onSubmit={async () => onDone(await client.requestPaper({ blueprint: bp, class: klass, opens_at: instant(opens), closes_at: instant(closes), forms: count, note: note.trim() }))}
      onClose={onClose}
    >
      <p className="muted small flush">{t("bank:reqLead")}</p>
      {noClass ? (
        <p className="warn small flush" data-testid="bank-request-no-class">
          {t("bank:reqNoClass")} <Link to="/classes">{t("bank:reqToClasses")}</Link>
        </p>
      ) : bps !== null && bps.length === 0 ? (
        <p className="warn small flush">{t("bank:reqNoBlueprint")}</p>
      ) : (
        <>
          {/* a blueprint's title is long, and what it draws decides the choice: radios that say both, never a select that cuts them */}
          <Choices
            name="bank-request-blueprint"
            className="bank-choice"
            legend={t("bank:reqBlueprint")}
            value={bp}
            onChange={setBp}
            options={(bps ?? []).map((b) => {
              const line = drawsLine(b);
              return {
                value: b.id,
                name: b.title,
                what: (
                  <>
                    {line && `${line} · `}
                    <Link to={`/bank/blueprints/${encodeURIComponent(b.id)}`}>{t("bank:reqBlueprintMore")}</Link>
                  </>
                ),
              };
            })}
            testid="bank-request-blueprints"
          />
          <label className="field">
            {t("bank:reqClass")}
            <select value={klass} onChange={(e) => setKlass(e.target.value)} aria-label={t("bank:reqClass")}>
              {me.user.classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          {/* before asking: whether the bank has what the blueprint asks for, for this class */}
          {chosen && klass && <Trial key={`${chosen.id}:${klass}`} client={client} b={chosen} classes={me.user.classes} fixed={klass} compact />}
          <div className="stack tight">
            <div className="field-pair">
              <label className="field">
                {t("bank:reqOpens")}
                <input type="datetime-local" value={opens} onChange={(e) => setOpens(e.target.value)} aria-label={t("bank:reqOpens")} />
              </label>
              <label className="field">
                {t("bank:reqCloses")}
                <input type="datetime-local" value={closes} onChange={(e) => setCloses(e.target.value)} aria-label={t("bank:reqCloses")} />
              </label>
            </div>
            <span className="muted small">{t("bank:reqWindowHint")}</span>
          </div>
          {most > 1 && (
            <label className="field">
              {t("bank:reqForms")}
              <input type="number" min={1} max={most} value={forms || ""} onChange={(e) => setForms(e.target.value === "" ? 0 : formsWithin(Number(e.target.value), most))} aria-label={t("bank:reqForms")} />
              <span className="muted small">{t("bank:reqFormsHint", { n: most })}</span>
            </label>
          )}
          <label className="field">
            {t("bank:reqNote")}
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} aria-label={t("bank:reqNote")} />
            <span className="muted small">{t("bank:reqNoteHint")}</span>
          </label>
          {chosen?.summary?.approval && <p className="warn small flush">{t("bank:reqApproval")}</p>}
        </>
      )}
      {error && <p className="error small flush">{t("common:error", { m: error })}</p>}
    </FormDialog>
  );
}

export const requestWord = (s: string) => word("bank:reqState", s) ?? s;
/**
 * What a request is called: the title of the blueprint version it recorded,
 * which the server sends with it (byj-1wv.101), else the blueprints list's
 * title for its id — the version that list shows, which may be a later one —
 * else the id.
 */
export const requestTitle = (r: PaperRequest, titleOf: Record<string, string>) => r.blueprint_title || titleOf[r.blueprint] || r.blueprint;
const refusedOrFailed = (s: string) => s === "failed" || s === "refused";

/** A regrade asked of the bank, with why: the instructor's answer to a dispute, or a check fixed. */
function RegradeDialog({ row, onSubmit, onClose }: { row: GradingRow; onSubmit: (reason: string) => Promise<void>; onClose: () => void }) {
  const [reason, setReason] = useState("");
  return (
    <FormDialog
      id="bank-regrade-dialog"
      testid="bank-regrade"
      title={t("bank:regradeTitle", { student: row.student, question: row.question })}
      submitLabel={t("bank:regrade")}
      canSubmit={reason.trim() !== ""}
      onSubmit={() => onSubmit(reason.trim())}
      onClose={onClose}
    >
      <label className="field">
        {t("bank:regradeWhy")}
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} aria-label={t("bank:regradeWhy")} />
      </label>
      <p className="muted small flush">{t("bank:regradeNote")}</p>
    </FormDialog>
  );
}

/** A paper's hand-ins as the bank grades them: each one's grade on Share, the bank's job on it, what grading spent. */
export function PaperGradingPanel({ client, paper }: { client: BankClient; paper: Paper }) {
  const [g, setG] = useState<PaperGrading | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [regrading, setRegrading] = useState<GradingRow | null>(null);
  const load = useCallback(async () => {
    try {
      setG(await client.paperGrading(paper.id));
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, paper.id]);
  useEffect(() => {
    void load();
  }, [load]);
  // a hand-in being graded is looked at again until it is
  useEffect(() => {
    if (!g?.submissions.some((r) => r.job && (r.job.state === "queued" || r.job.state === "running"))) return;
    const id = setTimeout(() => void load(), 3000);
    return () => clearTimeout(id);
  }, [g, load]);
  if (!g) return error ? <p className="error small">{t("common:error", { m: error })}</p> : <p className="muted small">{t("common:loading")}</p>;
  return (
    <div className="stack rows-panel" data-testid="bank-grading">
      {g.over_budget && <p className="warn small flush">{t("bank:overBudget", { spent: g.month_spend_usd.toFixed(2), budget: g.budget_usd.toFixed(2) })}</p>}
      <p className="muted small flush">{t("bank:gradingSpend", { usd: g.spend_usd.toFixed(2), n: g.submissions.length })}</p>
      {error && <p className="error small flush">{t("common:error", { m: error })}</p>}
      {g.submissions.length === 0 ? (
        <EmptyRows text={t("bank:gradingNone")} />
      ) : (
        <ul className="rows">
          {g.submissions.map((r) => (
            <li key={r.id} className={`share-row no-menu ${r.grade?.released ? "is-ended" : "is-live"}`} data-testid="bank-grading-row">
              <span className="row-lead" />
              <span className="kind-box" aria-hidden="true">
                {initialOf(r.student)}
              </span>
              <div className="row-main">
                <span className="row-title">{r.student}</span>
                <div className="row-sub">
                  {[
                    r.question,
                    r.grade && (r.grade.released ? t("bank:gradeReleased") : t("bank:gradeUnreleased")),
                    r.job && r.job.state !== "done" && (word("bank:jobState", r.job.state) ?? r.job.state),
                    r.job?.error,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div className="row-meta">
                <span className={`chip${r.grade ? "" : " chip-live"}`}>{r.grade ? t("bank:gradeScore", { score: r.grade.override ?? r.grade.score }) : t("bank:gradeWaiting")}</span>
                {r.grade && (
                  <button className="small" aria-haspopup="dialog" onClick={() => setRegrading(r)}>
                    {t("bank:regrade")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {regrading && (
        <RegradeDialog
          row={regrading}
          onSubmit={async (reason) => {
            await client.regrade(paper.id, regrading.id, reason);
            setRegrading(null);
            await load();
          }}
          onClose={() => setRegrading(null)}
        />
      )}
    </div>
  );
}

function RequestRow({ x, title, papers, admin, onDecide, onWithdraw }: {
  x: PaperRequest;
  /** what the request is called, requestTitle's: its recorded version's title, else the list's, else the id */
  title: string;
  papers: Paper[];
  admin: boolean;
  onDecide: (x: PaperRequest, approve: boolean) => void;
  onWithdraw: (x: PaperRequest) => void;
}) {
  const sub = [x.class_name, admin && x.requester_email, whenMinute(x.created_at), x.note].filter(Boolean);
  const menu: MenuItem[] = !admin && x.state === "pending" ? [{ label: t("bank:reqWithdraw"), onSelect: () => onWithdraw(x), danger: true }] : [];
  return (
    <li className={`share-row ${x.state === "pending" || x.state === "drawing" || x.state === "delivered" ? "is-live" : "is-ended"}`} data-testid="bank-paper-request">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        ppr
      </span>
      <div className="row-main">
        <span className="row-title">{title}</span>
        <div className="row-sub">{sub.join(" · ")}</div>
        {x.why && <p className={`small flush ${refusedOrFailed(x.state) ? "error" : ""}`}>{x.why}</p>}
        {/* each paper drawn for it, to its own page: its items, its grading, the exam on Share */}
        {papers.map((p) => (
          <p key={p.id} className="small flush bank-paper" data-testid="bank-paper">
            <Link to={`/bank/papers/${encodeURIComponent(p.id)}`}>{papers.length > 1 ? t("bank:paperFormN", { n: p.form }) : t("bank:paperOpen")}</Link>
            {" · "}
            {t("bank:paperItems", { n: p.items.length })}
            {p.exam_url && (
              <>
                {" · "}
                <a href={p.exam_url} target="_blank" rel="noreferrer">
                  {t("bank:paperOnShare")}
                </a>
              </>
            )}
          </p>
        ))}
      </div>
      <div className="row-meta">
        <span className={`chip${refusedOrFailed(x.state) ? " chip-warn" : x.state === "delivered" ? " chip-live" : ""}`}>{requestWord(x.state)}</span>
        {admin && x.state === "pending" && (
          <>
            <button className="small primary" onClick={() => onDecide(x, true)}>
              {t("bank:reqApprove")}
            </button>
            <button className="small" onClick={() => onDecide(x, false)}>
              {t("bank:reqRefuse")}
            </button>
          </>
        )}
      </div>
      <RowMenu label={t("shares:moreFor", { title })} items={menu} />
    </li>
  );
}

/** An admin's refusal of a request, with why: its requester reads it. */
function RefuseDialog({ x, onSubmit, onClose }: { x: PaperRequest; onSubmit: (why: string) => Promise<void>; onClose: () => void }) {
  const [why, setWhy] = useState("");
  return (
    <FormDialog
      id="bank-refuse-dialog"
      testid="bank-refuse"
      title={t("bank:reqRefuseTitle", { email: x.requester_email })}
      submitLabel={t("bank:reqRefuse")}
      canSubmit={why.trim() !== ""}
      onSubmit={() => onSubmit(why.trim())}
      onClose={onClose}
    >
      <label className="field">
        {t("bank:reqRefuseWhy")}
        <textarea value={why} onChange={(e) => setWhy(e.target.value)} rows={3} aria-label={t("bank:reqRefuseWhy")} />
      </label>
    </FormDialog>
  );
}

/** The requests and their papers; `request` given, its dialog is open (/bank/request). */
export function BankPapers({ client, me, request }: { client: BankClient; me: BankMe; request?: { blueprint?: string } }) {
  const admin = me.role === "admin";
  const [requests, setRequests] = useState<PaperRequest[] | null>(null);
  const [papers, setPapers] = useState<Paper[]>([]);
  const [titles, setTitles] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const [refusing, setRefusing] = useState<PaperRequest | null>(null);
  const [asking, setAsking] = useState<{ blueprint?: string } | null>(request ?? null);
  // the dialog follows the address: Back to /bank/request opens it, Back from it closes it
  const asked = request ? request.blueprint ?? "" : null;
  useEffect(() => {
    setAsking(asked === null ? null : { blueprint: asked || undefined });
  }, [asked]);
  const load = useCallback(async () => {
    try {
      const [rs, ps] = await Promise.all([client.requests(admin), client.papers(admin)]);
      setRequests(rs);
      setPapers(ps);
      setError(null);
    } catch (err) {
      setError(errText(err));
    }
  }, [client, admin]);
  useEffect(() => {
    void load();
  }, [load]);
  // the list's titles name a request the server sent without its recorded version's title
  useEffect(() => {
    client.blueprints().then(
      (l) => setTitles(Object.fromEntries(l.map((b) => [b.id, b.title]))),
      () => {},
    );
  }, [client]);
  // a request being drawn is looked at again until it is
  useEffect(() => {
    if (!requests?.some((x) => x.state === "drawing")) return;
    const id = setTimeout(() => void load(), 3000);
    return () => clearTimeout(id);
  }, [requests, load]);
  const decide = async (x: PaperRequest, approve: boolean) => {
    if (!approve) return setRefusing(x);
    try {
      await client.decide(x.id, true, "");
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };
  const withdraw = async (x: PaperRequest) => {
    if (!(await ask(t("bank:reqWithdrawAsk"), { action: t("bank:reqWithdraw"), danger: true }))) return;
    try {
      await client.withdrawRequest(x.id);
    } catch (err) {
      setError(errText(err));
    }
    await load();
  };
  const closeAsking = () => {
    setAsking(null);
    if (request) navigate("/bank/papers");
  };
  const all = requests ?? [];
  return (
    <div data-testid="bank-papers">
      <div className="panel-head">
        <p className="muted small panel-lead">{admin ? t("bank:papersLeadAdmin") : t("bank:papersLead")}</p>
        <button className="primary" aria-haspopup="dialog" aria-expanded={!!asking} aria-controls={asking ? requestDialogId : undefined} onClick={() => setAsking({})}>
          {t("bank:reqTitle")}
        </button>
      </div>
      {/* how a request becomes a graded exam, once, over the list */}
      <ol className="stage-steps bank-flow" aria-label={t("bank:papersFlowLabel")} data-testid="bank-papers-flow">
        {(["request", "approve", "draw", "added", "grade"] as const).map((k, i) => (
          <li key={k}>
            <span className="stage-mark" aria-hidden="true">
              {i + 1}
            </span>
            {t(`bank:papersFlow.${k}`)}
          </li>
        ))}
      </ol>
      {error && <p className="error">{t("common:error", { m: error })}</p>}
      {requests === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <Grouped<PaperRequest>
          id="bank-papers"
          groups={[
            { title: admin ? t("bank:papersWaitingAdmin") : t("bank:papersWaiting"), key: "pending", className: "rows-head-attention", items: all.filter((x) => x.state === "pending") },
            { title: t("bank:papersHead"), key: "rest", items: all.filter((x) => x.state !== "pending"), none: <EmptyRows text={all.length === 0 ? t("bank:papersNoneHow") : t("bank:papersNone")} /> },
          ]}
          row={(x) => (
            <RequestRow
              key={x.id}
              x={x}
              title={requestTitle(x, titles)}
              papers={papers.filter((p) => p.request === x.id)}
              admin={admin}
              onDecide={(r, a) => void decide(r, a)}
              onWithdraw={(r) => void withdraw(r)}
            />
          )}
        />
      )}
      {asking && (
        <RequestDialog
          client={client}
          me={me}
          blueprint={asking.blueprint}
          onDone={() => {
            closeAsking();
            void load();
          }}
          onClose={closeAsking}
        />
      )}
      {refusing && (
        <RefuseDialog
          x={refusing}
          onSubmit={async (why) => {
            await client.decide(refusing.id, false, why);
            setRefusing(null);
            await load();
          }}
          onClose={() => setRefusing(null)}
        />
      )}
      {confirmDialog}
    </div>
  );
}

/** A paper's page: the request it was drawn for, its items — each to its page — the exam on Share, and its grading. */
export function BankPaperPage({ client, id }: { client: BankClient; id: string }) {
  const [p, setP] = useState<Paper | null>(null);
  const [req, setReq] = useState<PaperRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const titles = useItemTitles(client);
  useEffect(() => {
    (async () => {
      try {
        const paper = await client.paper(id);
        setP(paper);
        setReq((await client.paperRequest(paper.request)).request);
      } catch (err) {
        setError(errText(err));
      }
    })();
  }, [client, id]);
  if (!p) return error ? <p className="error">{t("common:error", { m: error })}</p> : <p className="muted">{t("common:loading")}</p>;
  return (
    <section className="stack bank-item" data-testid="bank-paper-page">
      <p className="small flush">
        <Link to="/bank/papers" className="muted">
          ← {t("bank:backToPapers")}
        </Link>
      </p>
      <header className="page-head bank-item-head">
        <h2 className="flush">{p.title}</h2>
      </header>
      <StateBar
        className="exam-bar"
        label={t("bank:paperBar")}
        state="live"
        stateLabel={<span className="share-bar-state">{req ? requestWord(req.state) : requestWord("delivered")}</span>}
        testid="bank-paper-bar"
        actions={
          p.exam_url && (
            <a className="button small primary" href={p.exam_url} target="_blank" rel="noreferrer">
              {t("bank:paperOnShare")}
            </a>
          )
        }
      >
        <span className="share-bar-facts">
          {[req?.class_name, req?.requester_email, whenMinute(p.created_at), t("bank:paperItems", { n: p.items.length })].filter(Boolean).join(" · ")}
        </span>
      </StateBar>
      {error && <p className="error flush">{t("common:error", { m: error })}</p>}
      <p className="muted small flush">{t("bank:paperLead")}</p>
      <section className="stack tight">
        <h3 className="rows-head">{t("bank:paperItemsHead")}</h3>
        <ul className="rows" data-testid="bank-paper-items">
          {p.items.map((x) => (
            <li key={`${x.item}@${x.version}`} className="share-row no-menu is-live">
              <span className="row-lead" />
              <span className="kind-box" aria-hidden="true">
                itm
              </span>
              <div className="row-main">
                <Link to={`/bank/items/${encodeURIComponent(x.item)}`} className="row-title">
                  {titles[x.item] ?? x.id}
                </Link>
                <div className="row-sub">
                  {t("bank:versionN", { n: x.version })} · <code className="bank-target">{x.id}</code>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
      <section className="stack tight">
        <h3 className="rows-head">{t("bank:grading")}</h3>
        {p.exam ? <PaperGradingPanel client={client} paper={p} /> : <EmptyRows text={t("bank:gradingNone")} />}
      </section>
    </section>
  );
}
